import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { apiFetch } from "../lib/api";
import { formatDate } from "../lib/dates";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can } from "@family/shared";
import type {
  SessionEntry,
  SessionsListResponse,
} from "@family/shared";

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(d);
}

function locationString(s: SessionEntry): string {
  const parts = [s.city, s.region, s.country].filter(Boolean);
  return parts.join(", ") || "—";
}

function shortUA(ua: string | null): string {
  if (!ua) return "—";
  // Heuristic labelling — no full UA-parser pulled in.
  const m = ua.match(/(Chrome|Safari|Firefox|Edge|CriOS|FxiOS)\/[\d.]+/);
  const browser = m?.[1]
    ? m[1].replace("CriOS", "Chrome").replace("FxiOS", "Firefox")
    : "Unknown";
  let os = "Unknown";
  if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Windows/.test(ua)) os = "Windows";
  else if (/iPhone|iPad|iOS/.test(ua)) os = "iOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/Linux/.test(ua)) os = "Linux";
  return `${browser} / ${os}`;
}

export function SessionsPage() {
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;

  const sessionsQuery = useQuery<SessionsListResponse>({
    queryKey: ["sessions", slug],
    queryFn: () => apiFetch<SessionsListResponse>(`/families/${slug}/sessions`),
    refetchInterval: 30_000,
    enabled: Boolean(slug),
  });

  if (meQuery.isLoading) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  if (!primary || !can(primary.role, "sessions:view")) {
    return <Navigate to="/" replace />;
  }

  const sessions = sessionsQuery.data?.sessions ?? [];
  const active = sessions.filter((s) => s.active);
  const recent = sessions.filter((s) => !s.active);

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <div className="row-between">
          <h1>Sessions</h1>
          <span className="text-subtle" style={{ fontSize: "0.85rem" }}>
            auto-refresh · every 30s
          </span>
        </div>

        {sessionsQuery.isLoading && <p className="text-muted">Loading…</p>}
        {sessionsQuery.error && (
          <p className="text-error">
            Error: {(sessionsQuery.error as Error).message}
          </p>
        )}

        <section className="stack">
          <h2>
            Active now{" "}
            <span className="badge badge--admin" style={{ marginLeft: 8 }}>
              {active.length}
            </span>
          </h2>
          {active.length === 0 ? (
            <p className="text-muted">No active sessions.</p>
          ) : (
            <SessionTable rows={active} />
          )}
        </section>

        <section className="stack">
          <h2>Recent</h2>
          {recent.length === 0 ? (
            <p className="text-muted">Nothing yet.</p>
          ) : (
            <SessionTable rows={recent} />
          )}
        </section>
      </main>
    </>
  );
}

function SessionTable({ rows }: { rows: SessionEntry[] }) {
  return (
    <div
      style={{
        overflowX: "auto",
        border: "1px solid var(--c-border)",
        borderRadius: 8,
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.9rem",
        }}
      >
        <thead>
          <tr
            style={{
              background: "var(--c-bg-alt)",
              borderBottom: "1px solid var(--c-border)",
              textAlign: "left",
            }}
          >
            <Th>User</Th>
            <Th>IP</Th>
            <Th>Location</Th>
            <Th>Device</Th>
            <Th>Last seen</Th>
            <Th style={{ textAlign: "right" }}>Requests</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} style={{ borderTop: "1px solid var(--c-border)" }}>
              <Td>
                <strong>
                  {s.displayName ?? s.email ?? s.phone ?? s.userId.slice(0, 8)}
                </strong>
                {s.active && (
                  <span
                    title="Active"
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#22c55e",
                      marginLeft: 8,
                      verticalAlign: "middle",
                    }}
                  />
                )}
              </Td>
              <Td>
                <code style={{ fontSize: "0.85rem" }}>{s.ip}</code>
              </Td>
              <Td>{locationString(s)}</Td>
              <Td title={s.userAgent ?? ""}>{shortUA(s.userAgent)}</Td>
              <Td>{formatWhen(s.lastSeen)}</Td>
              <Td style={{ textAlign: "right" }}>{s.requestCount}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <th
      style={{
        padding: "10px 14px",
        fontWeight: 700,
        fontSize: "0.8rem",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: "var(--c-text-muted)",
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  style,
  title,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  title?: string;
}) {
  return (
    <td
      title={title}
      style={{
        padding: "10px 14px",
        verticalAlign: "middle",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </td>
  );
}
